import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { HoverVideo } from "@/components/VimeoEmbed";
import { projectPoster } from "@/lib/vimeo";
import { divisionColor } from "@/lib/divisions";
import { SITE } from "@/constants/testIds";

/** Card layouts per aspect — no technical chips, no index numbers. */
function HorizontalCard({ project, index }) {
  const right = index % 2 === 1;
  const [hovered, setHovered] = useState(false);
  const target = `/work/${project.slug}`;
  return (
    <motion.article
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-12%" }}
      transition={{ duration: 0.7, ease: [0.7, 0, 0.2, 1] }}
      data-testid={SITE.workCard}
      className="group grid grid-cols-12 gap-x-6 md:gap-x-10 items-center pb-16 md:pb-24"
    >
      <div className={`col-span-12 ${right ? "md:col-span-8 md:col-start-5" : "md:col-span-8"} relative`}>
        <Link
          to={target}
          className="block relative overflow-hidden bg-charcoal"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div className="relative w-full" style={{ aspectRatio: "16 / 9" }}>
            <img src={projectPoster(project)} alt={project.title} className="card-img absolute inset-0 w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <HoverVideo id={project.vimeo_id} active={hovered} overlay="bg-ink/15" />
          </div>
        </Link>
      </div>

      <div className={`col-span-12 ${right ? "md:col-span-4 md:col-start-1 md:row-start-1 md:pr-4" : "md:col-span-4 md:pl-4"} mt-6 md:mt-0`}>
        <p className="eyebrow" style={{ color: divisionColor(project.division) }}>
          {project.division ? `RELATABLE ${project.division.toUpperCase()}` : "Selected Work"}
        </p>
        <h3 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl tracking-tighter font-medium leading-[1] text-bone">
          {project.title}
        </h3>
        <Link to={target} className="mt-6 inline-flex items-center gap-2 eyebrow text-bone link-line">
          See The Film <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </motion.article>
  );
}

function VerticalCard({ project }) {
  const [hovered, setHovered] = useState(false);
  const target = `/work/${project.slug}`;
  return (
    <motion.article
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-12%" }}
      transition={{ duration: 0.7, ease: [0.7, 0, 0.2, 1] }}
      data-testid={SITE.workCard}
      className="group grid grid-cols-12 gap-x-6 md:gap-x-10 items-end pb-16 md:pb-24"
    >
      <div className="col-span-12 md:col-span-5 md:col-start-4 relative">
        <Link
          to={target}
          className="block relative overflow-hidden bg-charcoal mx-auto"
          style={{ maxWidth: 460 }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div className="relative w-full" style={{ aspectRatio: "9 / 16" }}>
            <img src={projectPoster(project)} alt={project.title} className="card-img absolute inset-0 w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <HoverVideo id={project.vimeo_id} active={hovered} overlay="bg-ink/15" />
          </div>
        </Link>
      </div>
      <div className="col-span-12 md:col-span-3 md:col-start-9 mt-6 md:mt-0 md:pb-8">
        <p className="eyebrow" style={{ color: divisionColor(project.division) }}>
          {project.division ? `RELATABLE ${project.division.toUpperCase()}` : "Selected Work"}
        </p>
        <h3 className="mt-4 font-display text-3xl md:text-4xl tracking-tighter font-medium leading-[1] text-bone">
          {project.title}
        </h3>
        <Link to={target} className="mt-6 inline-flex items-center gap-2 eyebrow text-bone link-line">
          See The Film <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </motion.article>
  );
}

export default function SelectedWork({ projects = [] }) {
  return (
    <section data-testid={SITE.selectedWork} className="bg-ink text-bone py-20 md:py-24 lg:py-28">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 md:mb-20 gap-6">
          <div>
            <p className="eyebrow text-fog mb-5">◇ Selected Work</p>
            <h2 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter font-medium leading-[0.92]">
              Films we&rsquo;d show<br/>at the dinner table.
            </h2>
          </div>
          <p className="max-w-md text-fog text-base md:text-lg leading-relaxed">
            A curated selection from our recent slate. The full library lives on the <Link to="/work" className="text-bone link-line">Work page</Link>.
          </p>
        </div>

        <div>
          {projects.map((p, i) =>
            (p.aspect || "16:9") === "9:16"
              ? <VerticalCard key={p.id || p.slug} project={p} />
              : <HorizontalCard key={p.id || p.slug} project={p} index={i} />
          )}
        </div>

        <div className="mt-4 flex justify-center">
          <Link
            to="/work"
            className="inline-flex items-center gap-3 px-7 py-4 border border-line text-bone eyebrow hover:border-bone hover:bg-bone/5 transition-colors"
          >
            See All Work <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
